import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { C, brl, brlFull, num, pct } from './theme';
import { Panel } from './KpiGrid';

const Stat = ({ id, label, value, sub, color, path }) => {
  const navigate = useNavigate();
  return (
    <button type="button" onClick={() => navigate(path)} data-testid={`fin-${id}`}
      className="min-w-0 rounded-2xl border p-3.5 text-left transition-[transform,background-color] duration-150 hover:-translate-y-0.5 hover:bg-[#FBF9FD]" style={{ borderColor: C.line }}>
      <span className="block h-1 w-8 rounded-full" style={{ background: color }} />
      <p className="mt-2.5 truncate text-base sm:text-lg font-extrabold tabular-nums" style={{ color: C.ink, fontFamily: 'Manrope, sans-serif' }} data-testid={`fin-${id}-value`}>{value}</p>
      <p className="text-[12px] font-semibold" style={{ color: C.aubergine }}>{label}</p>
      {sub && <p className="truncate text-[11px]" style={{ color: C.muted }}>{sub}</p>}
    </button>
  );
};

export function FinanceBlock({ fin, year }) {
  const hasMonthly = fin.monthly.some((m) => m.previsto || m.recebido);
  return (
    <Panel kicker="Financeiro" title={`Receita do ano letivo ${year}`} testid="block-finance"
      right={<span className="text-[11px]" style={{ color: C.muted }} data-testid="fin-titles-count">{num(fin.titles_count)} títulos no ano</span>}>
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
        <Stat id="received" label="Receita recebida" value={brl(fin.received)} sub={`${num(fin.paid_count)} pagos`} color={C.green} path="/v2/financeiro/titulos" />
        <Stat id="expected" label="Receita prevista" value={brl(fin.expected)} sub={fin.expected ? `${pct(fin.received * 100 / fin.expected)} realizado` : 'Sem títulos'} color={C.purple} path="/v2/financeiro/titulos" />
        <Stat id="open" label="Títulos em aberto" value={num(fin.open_count)} sub={brl(fin.open)} color={C.amber} path="/v2/financeiro/titulos" />
        <Stat id="overdue" label="Vencidos" value={num(fin.overdue_count)} sub={brl(fin.overdue)} color={C.orange} path="/v2/financeiro/inadimplentes" />
        <Stat id="today" label="Vencendo hoje" value={num(fin.due_today_count)} sub={brl(fin.due_today)} color={C.magenta} path="/v2/financeiro/titulos" />
        <Stat id="delinquency" label="Inadimplência" value={pct(fin.delinquency_pct)} sub="vencido ÷ (recebido + vencido)" color={fin.delinquency_pct >= 5 ? C.orange : C.lime} path="/v2/financeiro/inadimplentes" />
      </div>
      <p className="mb-1 mt-5 text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: C.muted }}>Prevista x recebida por mês</p>
      {!hasMonthly ? <p className="py-6 text-center text-sm" style={{ color: C.muted }} data-testid="fin-monthly-empty">Sem títulos com vencimento ou pagamento em {year}.</p> : (
        <div className="h-[220px]" data-testid="fin-monthly-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={fin.monthly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={3}>
              <CartesianGrid vertical={false} stroke={C.line} />
              <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fill: C.muted, fontSize: 11 }} />
              <YAxis tickLine={false} axisLine={false} width={64} tick={{ fill: C.muted, fontSize: 10 }} tickFormatter={(v) => brl(v).replace('R$', '').trim()} />
              <Tooltip cursor={{ fill: `${C.purple}0d` }} formatter={(v, n) => [brlFull(v), n]} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="previsto" name="Prevista" fill={C.purple} radius={[6, 6, 0, 0]} maxBarSize={18} />
              <Bar dataKey="recebido" name="Recebida" fill={C.green} radius={[6, 6, 0, 0]} maxBarSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Panel>
  );
}
